import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Alert, 
  InputAdornment, 
  IconButton, 
  Link, 
  CircularProgress, 
  FormControlLabel, 
  Checkbox,
  
  Fade,
  Card,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon,
  AdminPanelSettings,
  Security,
 
  Hotel
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import backgroundImage from '../../../assets/images/hot.webp';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f8f9fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#1976d2',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          borderRadius: 8,
          boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.4)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: '#1976d2',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

const LoginAdminStaff = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Load email from localStorage if rememberMe is true
  useEffect(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, []);

  // Handle form submission - Updated to use the same API as EneuronLogin
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Both email and password are required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email.');
      return;
    }

    setLoading(true);

    try {
      console.log('Attempting login...');

      // Using the same API endpoint as EneuronLogin
      const loginResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/eneurons/login.php`,
        { 
          email: email.trim(), 
          password: password 
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        }
      );

      console.log('Login response:', loginResponse.data);

      if (loginResponse.data.access_token) {
        const authToken = loginResponse.data.access_token;
        localStorage.setItem('authToken', authToken);
        
        // Store user data directly from login response
        const userProfile = loginResponse.data.user;
        localStorage.setItem('userProfile', JSON.stringify(userProfile));
        
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('email', email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('email');
        }

        // Redirect based on user role or type
        if (userProfile.user_type === 'hotel_admin') {
          navigate('/hotel/admin/dashboard');
        } else {
          navigate('/HotelAdmin');
        }
        
        setError('');
      } else {
        setError('Invalid response from server.');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.response && err.response.data) {
        setError(err.response.data.error || 'Invalid credentials.');
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout. Please try again.');
      } else if (err.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Validate email format
  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          padding: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Professional overlay matching HotelWebsite theme */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(33, 150, 243, 0.8) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={true} timeout={800}>
            <Card
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 20px 60px 0 rgba(25, 118, 210, 0.25)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1565c0 100%)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white',
                    mb: 3,
                    boxShadow: '0 8px 25px 0 rgba(25, 118, 210, 0.4)',
                  }}
                >
                  <Hotel sx={{ fontSize: 40 }} />
                </Box>
                
                <Typography 
                  component="h1" 
                  variant="h4" 
                  sx={{ 
                    textAlign: 'center',
                    mb: 1,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold',
                  }}
                >
                  Hotel Management 
                </Typography>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center',
                    maxWidth: '90%',
                    mb: 2,
                    fontWeight: 500,
                  }}
                >
                  Admin Portal
                </Typography>

                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'primary.main',
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  Administrative Access
                </Typography>
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'error.light',
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdminPanelSettings color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleTogglePassword} 
                          edge="end" 
                          aria-label="Toggle password visibility"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 4 
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        color="primary"
                        sx={{ 
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          '&.Mui-checked': {
                            color: 'primary.main',
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        Remember me
                      </Typography>
                    }
                  />
                  
                  <Link 
                    href="#" 
                    variant="body2" 
                    underline="hover" 
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={loading ? null : <LoginIcon />}
                  disabled={loading || !email || !password}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </Button>
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Secure access for authorized hotel staff only
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  © {new Date().getFullYear()} e-Neurons Luxury Hospitality System
                </Typography>
              </Box>
            </Card>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LoginAdminStaff;